import React from 'react'
import { css } from 'glamor'
import '../style/global'

import colors from '../style/colors'
import { fontSize, fontWeight } from '../style/sizes'
import { SMALL_SCREEN_BREAKPOINT, MID_SCREEN_BREAKPOINT, mediaMaxWidth } from '../style/mixins'
import Container from '../components/container'
import Footer from '../components/footer'
import ProductList from '../components/productList'
import gdprImage from '../assets/gdpr.svg'

const CSS = {
  page: css({
    overflowX: 'hidden'
  }),
  topBackground: css({
    position: 'absolute',
    zIndex: -1,
    top: 0,
    left: 0,
    right: 0,
    height: '1000px',
    overflow: 'hidden',
    textAlign: 'right',
    ':before': {
      content: `''`,
      position: 'absolute',
      top: '-520px',
      left: '-1000px',
      right: '-1000px',
      overflow: 'hidden',
      height: '1273px',
      backgroundColor: colors.blue,
      backgroundImage: 'radial-gradient(circle at bottom right, #1A31AE 0%, #0054FF 70%)',
      transform: 'rotate(12.2deg)',
      zIndex: -1,
      [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
        top: '-450px'
      }
    }
  }),
  topBar: css({
    color: colors.white,
    marginTop: '54px',
    fontSize: '27px',
    fontWeight: fontWeight.bold,
    textTransform: 'uppercase',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '50px'
  }),
  topElement: css({
    marginTop: '120px',
    marginBottom: '80px',
    [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
      textAlign: 'center'
    },
    ' h1': {
      fontSize: '40px',
      fontWeight: fontWeight.bold,
      lineHeight: '55px',
      color: colors.white,
      maxWidth: '615px',
      marginBottom: 0,
      [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
        fontSize: '30px',
        lineHeight: '35px',
        maxWidth: '340px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }
    },
    ' h2, h3': {
      fontSize: '20px',
      fontWeight: fontWeight.normal,
      lineHeight: '29px',
      color: colors.white,
      maxWidth: '490px',
      marginTop: '10px',
      marginBottom: '20px',
      [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
        marginLeft: 'auto',
        marginRight: 'auto'
      },
      [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
        fontSize: fontSize.medium,
        lineHeight: '21px',
        maxWidth: '340px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }
    },
    ' h3': {
      fontWeight: fontWeight.bold
    },
    ' hr': {
      width: '240px',
      height: '5px',
      backgroundColor: colors.white,
      border: 0,
      margin: 0,
      marginBottom: '20px',
      marginLeft: '2px',
      [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
        margin: 'auto'
      }
    }
  }),
  GDPRImageContainer: css({
    textAlign: 'center',
    display: 'block',
    [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
      display: 'none'
    }
  }),
  mainScreenshot: css({
    maxWidth: 'none',
    marginTop: '35px',
    [mediaMaxWidth(MID_SCREEN_BREAKPOINT)]: {
      maxWidth: '100%',
      marginTop: '50px'
    }
  }),
  featureImage: css({
    position: 'relative',
    width: '791px',
    maxWidth: '100%',
    height: '505px',
    maxHeight: '100%',
    [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
      height: 'auto'
    }
  }),
  featureImageBack: css({
    position: 'absolute',
    maxWidth: 'none',
    left: '-5%',
    top: '-15%',
    zIndex: 1,
    [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
      display: 'none'
    }
  }),
  featureImageMain: css({
    position: 'absolute',
    background: '#ffffff',
    zIndex: 2,
    boxShadow: '0 13px 21px 0 rgba(0,0,0,0.16)',
    ' > img:first-child': {
      display: 'block'
    },
    [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
      position: 'static',
      height: 'auto',
      width: 'auto'
    }
  }),
  featureImageFront: css({
    position: 'absolute',
    right: '-3%',
    bottom: '-13%',
    zIndex: 3,
    [mediaMaxWidth(SMALL_SCREEN_BREAKPOINT)]: {
      display: 'none'
    }
  }),
  addYourCompany: css({
    display: 'inline-block',
    height: '50px',
    width: '211px',
    borderRadius: '100px',
    color: colors.navy,
    backgroundColor: colors.cyan,
    fontWeight: fontWeight.bold,
    fontSize: '14px',
    lineHeight: '50px',
    textTransform: 'uppercase',
    textAlign: 'center'
  })
}

const TopBackground = () => <div {...CSS.topBackground} />

const TopBar = () => (
  <div {...CSS.topBar}>
    <div>GDPR SaaS Index</div>
    <div><a href='https://airtable.com/shrlC2aiyejy5iPIT' target='_blank' {...CSS.addYourCompany}>Add your company</a></div>
  </div>
)

const TopElement = () => (
  <div {...CSS.topElement}>
    <div className='row'>
      <div className='col-xs-12 col-md-8'>
        <h1>Make sure your SaaS vendors are GDPR ready</h1>
        <h2>A curated list of GDPR statements as published by the SaaS vendors</h2>
        <hr />
        <h3>GDPR effective date: May 25, 2018</h3>
      </div>
      <div className='col-xs-12 col-md-4' {...CSS.GDPRImageContainer}>
        <img alt='GDPR' src={gdprImage} />
      </div>
    </div>
  </div>
)

export default ({ data }) => (
  <div {...CSS.page}>
    <TopBackground />
    <Container>
      <TopBar />
      <TopElement />
      <ProductList data={data.allGdprCsv.edges} />
    </Container>
    <Footer />
  </div>
)

export const query = graphql`
  query AboutQuery {
    allGdprCsv {
      edges {
        node {
          Name,
          Website,
          GDPRLink,
          Image,
          TopApp
        }
      }
    }
  }
`



// WEBPACK FOOTER //
// ./src/pages/index.js